import '../ui/museum.css';
import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { ITEMS, KINDS, hasFound, foundCount, collectionStats } from '../core/collection.js';
import { questsForGiver, acceptQuest, turnIn, isReady, objectiveProgress, QUESTS } from '../core/quests.js';

// Toast Town Aquarium — fish you've caught get their own tank.
// Mirrors the Museum layout but with bubbling water pedestals.

export function createAquariumScene(){
  return () => new AquariumScene();
}

const WALL   = 0xD8F0F5;
const WALL_D = 0xA8D0DB;
const FLOOR  = 0xB8E0E8;
const TANK   = 0x6BB8D6;

const RARITY_COLOR = { common: 0xCFD6DB, uncommon: 0x7FBF6A, rare: 0x3D5FC0, legendary: 0xF4B233 };

class AquariumScene {
  constructor(){
    this.panels = [];
    this.bubbles = [];

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xE8F6FA);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 13;
    this.camTarget = new THREE.Vector3(0, 1.6, 2);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildHall();

    this.tankGroup = new THREE.Group();
    this.scene.add(this.tankGroup);
    this._buildTanks();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 7.5);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.curator = this._makeCurator();
    this.scene.add(this.curator.root);

    this.deskPos = new THREE.Vector3(-4.2, 0, 5.6);
    this.caseSpots = [];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFFFFF, 0.68));
    this.scene.add(new THREE.HemisphereLight(0xE0F8FF, 0xA8D0DB, 0.55));
    const sky = new THREE.DirectionalLight(0xFFF8E8, 0.65);
    sky.position.set(2, 16, 6); sky.castShadow = true;
    sky.shadow.mapSize.set(2048, 2048);
    const s = 20;
    sky.shadow.camera.left = -s; sky.shadow.camera.right = s;
    sky.shadow.camera.top = s; sky.shadow.camera.bottom = -s;
    this.scene.add(sky);
  }

  _buildHall(){
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(26, 20), new THREE.MeshLambertMaterial({ color: FLOOR }));
    floor.rotation.x = -Math.PI/2; floor.position.set(0, 0, -1); floor.receiveShadow = true;
    this.scene.add(floor);

    // wave-pattern tiles
    for (let gx = -11; gx <= 11; gx += 4)
      for (let gz = -8; gz <= 6; gz += 4){
        if (((gx + gz) / 4) % 2) continue;
        const tile = new THREE.Mesh(new THREE.PlaneGeometry(3.8, 3.8), new THREE.MeshLambertMaterial({ color: WALL_D }));
        tile.rotation.x = -Math.PI/2; tile.position.set(gx, 0.005, gz); tile.receiveShadow = true;
        this.scene.add(tile);
      }

    const back = new THREE.Mesh(new THREE.PlaneGeometry(26, 9), new THREE.MeshLambertMaterial({ color: WALL }));
    back.position.set(0, 4.5, -10); this.scene.add(back);
    box(26, 0.5, 0.5, 0x4A7A8A, 0, 3.4, -9.9, this.scene);

    const sideL = new THREE.Mesh(new THREE.PlaneGeometry(20, 9), new THREE.MeshLambertMaterial({ color: WALL_D }));
    sideL.rotation.y = Math.PI/2; sideL.position.set(-12, 4.5, -1); this.scene.add(sideL);
    const sideR = sideL.clone(); sideR.rotation.y = -Math.PI/2; sideR.position.set(12, 4.5, -1); this.scene.add(sideR);

    // ceiling light panels
    const glass = new THREE.MeshBasicMaterial({ color: 0xE8F8FF });
    for (let x = -8; x <= 8; x += 4)
      for (let z = -7; z <= 4; z += 4){
        const panel = new THREE.Mesh(new THREE.PlaneGeometry(3.2, 2.8), glass);
        panel.rotation.x = Math.PI/2; panel.position.set(x, 8.2, z); this.scene.add(panel);
      }

    const marquee = textSprite('🐠 TOAST TOWN AQUARIUM', 28, '#2E5A6A', 6.0, 1.15);
    marquee.position.set(0, 7.1, -9.7); this.scene.add(marquee);

    // exit arch
    cyl(0.18, 0.18, 3.4, 0x8C6239, -2.2, 1.7, 8.6, this.scene, 10);
    cyl(0.18, 0.18, 3.4, 0x8C6239,  2.2, 1.7, 8.6, this.scene, 10);
    box(5.2, 0.45, 0.4, 0x8C6239, 0, 3.5, 8.6, this.scene);
    const exitSign = textSprite('← back to beach', 24, '#2E5A6A', 3.6, 0.85);
    exitSign.position.set(0, 4.1, 8.6); this.scene.add(exitSign);

    // curator desk
    const desk = new THREE.Group(); desk.position.copy(this.deskPos); this.scene.add(desk);
    box(2.4, 1.1, 0.9, C.rye, 0, 0.55, 0, desk);
    box(2.6, 0.14, 1.05, C.crustDark, 0, 1.16, 0, desk);
    const plaque = textSprite('📋 Marina', 22, '#2E5A6A', 2.0, 0.7);
    plaque.position.set(-4.2, 1.9, 5.6); this.scene.add(plaque);
  }

  _buildTanks(){
    this.caseSpots = [];
    const ids = Object.keys(ITEMS).filter(id => ITEMS[id].kind === 'fish');
    const cols = [-6, -2, 2, 6];
    const rows = [-3, -6];
    const slots = [];
    for (const z of rows) for (const x of cols) slots.push({ x, z });

    ids.forEach((id, i) => {
      const slot = slots[i];
      if (!slot) return;
      this._tank(id, slot.x, slot.z);
    });
  }

  _tank(id, x, z){
    const item = ITEMS[id];
    const filled = hasFound(id);
    const g = new THREE.Group(); g.position.set(x, 0, z); this.tankGroup.add(g);

    // stone pedestal
    cyl(0.54, 0.6, 0.22, 0x8A9A9A, 0, 0.11, 0, g, 18);
    cyl(0.38, 0.42, 1.1, 0xA8B8B8, 0, 0.76, 0, g, 18);
    cyl(0.52, 0.5, 0.14, 0x8A9A9A, 0, 1.38, 0, g, 18);

    // glass tank
    const tank = new THREE.Mesh(
      new THREE.BoxGeometry(0.96, 1.0, 0.96),
      new THREE.MeshPhongMaterial({ color: TANK, transparent: true, opacity: 0.28, shininess: 80 })
    );
    tank.position.set(0, 1.95, 0); g.add(tank);

    if (filled){
      // fish icon swimming inside
      const fish = textSprite(item.icon, 40, '#000000', 0.8, 0.8);
      fish.position.set(0, 2.15, 0); g.add(fish);
      this.bubbles.push({ el: fish, base: fish.position.clone(), speed: 1.2 + Math.random() });
      const plate = textSprite(item.name, 18, '#2E5A6A', 2.1, 0.6);
      plate.position.set(0, 0.65, 0.55); g.add(plate);
      // rarity gem
      const col = RARITY_COLOR[item.rarity] || 0xCFD6DB;
      const gem = new THREE.Group(); gem.position.set(0, 2.7, 0);
      cyl(0, 0.16, 0.24, col, 0, 0.12, 0, gem, 6);
      cyl(0.16, 0, 0.16, col, 0, -0.08, 0, gem, 6);
      g.add(gem);
    } else {
      const q = textSprite('?', 48, '#7FA0AA', 0.8, 0.8);
      q.position.set(0, 2.05, 0); g.add(q);
      const plate = textSprite('? ? ?', 18, '#7FA0AA', 2.0, 0.6);
      plate.position.set(0, 0.65, 0.55); g.add(plate);
    }

    this.caseSpots.push({ pos: new THREE.Vector3(x, 0, z + 1.0), r: 1.7, filled, id });
  }

  _makeCurator(){
    const build = (g) => {
      const body = cyl(0.28, 0.26, 0.9, 0xF2D23B, 0, 0.45, 0, g, 12);
      sphere(0.26, 0xF2D23B, 0, 0.95, 0, g, 12);
      sphere(0.06, 0x2E2018, -0.1, 1.0, 0.2, g, 8);
      sphere(0.06, 0x2E2018,  0.1, 1.0, 0.2, g, 8);
      cyl(0.04, 0.04, 0.34, 0x5A4A2A, -0.35, 0.5, 0.1, g, 6).rotation.z = -0.6;
      cyl(0.04, 0.04, 0.34, 0x5A4A2A,  0.35, 0.5, 0.1, g, 6).rotation.z = 0.6;
      // little captain's hat
      cyl(0.4, 0.4, 0.06, 0xFFFFFF, 0, 1.38, 0, g, 16);
      cyl(0.18, 0.2, 0.16, 0xFFFFFF, 0, 1.5, 0, g, 12);
    };
    return new Townsperson({
      name: 'Marina', line: 'every fish has a story.',
      pos: new THREE.Vector3(-4.2, 0, 4.6), facing: -Math.PI * 0.6,
      wander: 0, speed: 0, bubbleY: 2.4, build
    });
  }

  _talkToCurator(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.curator.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._openDesk();
  }

  _openDesk(){
    this._clearPanels();
    this.nearSpot = null;
    this.input.onTap = null;
    HUD.showEnter(null);
    this.bread.drawFace('yum');
    const st = collectionStats();
    const fishIds = Object.keys(ITEMS).filter(id => ITEMS[id].kind === 'fish');
    const fishFound = fishIds.filter(hasFound).length;

    const { available, active } = questsForGiver('marina');
    const questRows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isReady(id)){
        questRows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>objective complete!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        const p = objectiveProgress(id);
        questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${p.have} / ${p.goal} ${q.objective.noun}</span></div><span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      const rew = q.reward?.crumbs ? ` · reward 🍞 ${q.reward.crumbs}` : '';
      questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}${rew}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    const qbody = questRows.length ? questRows.join('') : '<p class="q-dnone">"The tanks are looking lovely. Thanks for the donations!"</p>';

    const p = this._panel(`
      <div class="mus-card">
        <h2>Marina's Desk</h2>
        <div class="mus-sub">"Every catch becomes a little exhibit."</div>
        <div class="mus-prestige-big">species on display: <b>${fishFound}/${fishIds.length}</b></div>
        <div class="mus-prestige-big">total exhibits: <b>${st.found}/${st.total}</b></div>
        <div class="q-drows">${qbody}</div>
        <button class="mus-btn ghost close">← back to the tanks</button>
      </div>`);
    p.querySelector('.close').onclick = () => this._closeDesk();
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this._openDesk(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => { turnIn(b.dataset.id); HUD.refreshCrumbs(); this._openDesk(); });
  }

  _closeDesk(){
    this._clearPanels();
    this.input.onTap = (ndc, ray) => this._tap(ray);
  }

  _inspectCase(s){
    const item = ITEMS[s.id];
    if (s.filled){
      const n = foundCount(s.id);
      HUD.say(`${item.icon} ${item.name} — ${item.rarity} · ${n} caught.`);
      this.bread.drawFace('yum');
    } else {
      HUD.say('An empty tank — catch this fish at Sandy Toes Beach to fill it!');
      this.bread.drawFace('o');
    }
  }

  _panel(html){
    const el = document.createElement('div');
    el.className = 'mus-modal';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  _hotspots(){
    return [
      { pos: this.deskPos, r: 1.9, label: '📋 visit Marina\'s desk', action: () => this._talkToCurator() },
      ...this.caseSpots.map(s => ({
        pos: s.pos, r: s.r,
        label: s.filled ? '🔍 inspect the tank' : '🔍 peek at the empty tank',
        action: () => this._inspectCase(s)
      })),
      { pos: new THREE.Vector3(0, 0, 8.4), r: 2.2, label: '← back to the beach', action: () => this.manager.goTo('beach', { from: 'aquarium' }) }
    ];
  }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.3, 0.66];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('toast town aquarium');
    HUD.say('The Aquarium! Fish you catch at the beach go on display here. Talk to Marina for donations and quests.');
    HUD.setHint('click to walk · walk up to a tank or Marina\'s desk');
    HUD.hideHintSoon(8000);
  }

  exit(){
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _tap(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-12, Math.min(12, gp.x));
    gp.z = Math.max(-8.2, Math.min(8.4, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  update(dt){
    this.bread.update(dt);

    // bob fish icons gently
    for (const b of this.bubbles){
      b.el.position.y = b.base.y + Math.sin(performance.now() * 0.002 * b.speed) * 0.08;
      b.el.position.x = b.base.x + Math.cos(performance.now() * 0.0015 * b.speed) * 0.08;
    }

    if (!this.panels.length){
      const spots = this._hotspots();
      let near = null, nd = Infinity;
      spots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
      if (near && nd < near.r){
        const key = near.label;
        if (this.nearSpot !== key){ this.nearSpot = key; HUD.showEnter(near.label, () => near.action()); }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
